import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, UploadCloud, BadgeCheck, Wallet, FileMusic } from "lucide-react";
import { api, eur } from "../lib/api";
import { SEO } from "../components/SEO";
import { Reveal } from "../components/Reveal";

const STEPS = [
  { n: "01", icon: FileMusic, title: "Inscription", text: "Créez votre compte en cochant « Je suis un artiste » et choisissez votre nom de scène. C'est gratuit et sans engagement." },
  { n: "02", icon: BadgeCheck, title: "Validation", text: "Le studio écoute et valide chaque inscription pour garantir la qualité du catalogue. Vous êtes notifié dès l'ouverture de votre espace." },
  { n: "03", icon: UploadCloud, title: "Publication", text: "Envoyez vos morceaux (mp3 ou wav), fixez vos prix, choisissez les usages visés. Sans jaquette, une pochette unique est créée automatiquement à partir du titre." },
  { n: "04", icon: Wallet, title: "Ventes & reversements", text: "À chaque licence vendue, votre part vous est reversée par le studio. Le détail de chaque vente est visible dans votre espace, en toute transparence." },
];

const FAQ = [
  { q: "Qui peut rejoindre La Console des Artistes ?", a: "Tout compositeur ou producteur disposant des droits complets sur ses créations. Chaque inscription est validée à l'écoute par le studio." },
  { q: "Quels formats sont acceptés ?", a: "Fichiers mp3 ou wav de la meilleure qualité possible. La jaquette est optionnelle : une pochette sur mesure peut être générée automatiquement." },
  { q: "Que peuvent faire les acheteurs de mes morceaux ?", a: "Chaque achat correspond à une licence de synchronisation non exclusive pour un projet (publicité, film, production d'artiste ou contenu pour les réseaux)." },
  { q: "Combien coûte l'inscription ?", a: "Rien. L'inscription et la visibilité dans le catalogue sont entièrement gratuites. La plateforme se rémunère uniquement par une commission de 15 % sur les ventes réalisées." },
];

const DevenirArtiste = () => {
  const [offer, setOffer] = useState({ monthly_fee: 7, annual_fee: 60, commission_rate: 15 });

  useEffect(() => {
    api.get("/config/offer").then((r) => setOffer(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="devenir-artiste-page" className="mx-auto max-w-5xl px-5 pb-24 pt-32 sm:px-8">
      <SEO title="Devenir artiste" path="/devenir-artiste"
           description="Publiez et vendez vos musiques en licence sur La Console des Artistes : inscription gratuite, 85 % des ventes pour vous, sans abonnement." />
      <Reveal>
        <p className="text-xs uppercase tracking-[0.35em] text-amber-500">Devenir artiste</p>
        <h1 className="mt-3 font-display text-4xl font-black tracking-tighter sm:text-6xl">
          Vendez votre musique, <span className="italic text-amber-500">nous faisons le reste</span>
        </h1>
        <p className="mt-6 max-w-2xl text-sm leading-relaxed text-zinc-400 sm:text-base">
          Voici, en toute transparence, les conditions pour figurer sur La Console des Artistes.
        </p>
      </Reveal>

      <div className="mt-16 space-y-0">
        {STEPS.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.06}>
            <div className="grid gap-4 border-t border-white/[0.08] py-10 md:grid-cols-12" data-testid={`step-${s.n}`}>
              <span className="text-outline font-display text-6xl font-black md:col-span-2">{s.n}</span>
              <div className="md:col-span-10 md:pl-6">
                <h3 className="flex items-center gap-3 font-display text-xl font-medium italic sm:text-2xl">
                  <s.icon className="h-5 w-5 text-amber-500" /> {s.title}
                </h3>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-zinc-400">{s.text}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal>
        <div className="mt-16 border border-amber-500/30 bg-amber-500/5 p-8 sm:p-10" data-testid="conditions-block">
          <h2 className="font-display text-2xl font-medium">Les conditions, simplement</h2>
          <ul className="mt-6 space-y-4 text-sm leading-relaxed text-zinc-300">
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Gratuité totale :</strong> l'inscription et la visibilité sur le site sont entièrement gratuites — aucun abonnement, aucun frais caché.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Commission :</strong> la plateforme prélève {offer.commission_rate}% sur chaque vente. Le reste vous est reversé par le studio. Si vous ne vendez rien, vous ne payez rien.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Vos prix :</strong> vous fixez librement le prix de chaque licence.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Contrat :</strong> un accord de principe vous est remis à la validation — vous confirmez notamment que vos œuvres vous appartiennent intégralement. Il est à renvoyer signé sous 1 mois, directement depuis votre espace.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Reversements :</strong> le règlement de vos ventes est automatique, tous les 30 jours, sur votre compte PayPal — et vous êtes notifié à chaque versement.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Visibilité :</strong> page vitrine à votre nom, présence dans le catalogue, paiements et téléchargements gérés par la plateforme.</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
              <span><strong className="text-white">Souplesse :</strong> aucun engagement — vous pouvez retirer vos morceaux ou quitter la plateforme à tout moment.</span>
            </li>
          </ul>
        </div>
      </Reveal>

      <div className="mt-16" data-testid="faq-section">
        <Reveal>
          <h2 className="font-display text-2xl font-medium">Questions fréquentes</h2>
        </Reveal>
        <div className="mt-8 divide-y divide-white/[0.08] border-y border-white/[0.08]">
          {FAQ.map((f) => (
            <Reveal key={f.q}>
              <div className="py-6">
                <p className="font-display text-base font-medium sm:text-lg">{f.q}</p>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{f.a}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>

      <Reveal>
        <div className="mt-16 border border-white/[0.08] bg-surface p-10 text-center">
          <p className="font-display text-2xl font-black tracking-tight sm:text-3xl">Prêt à rejoindre le catalogue ?</p>
          <Link
            to="/connexion"
            data-testid="devenir-cta"
            className="group mt-8 inline-flex items-center gap-3 bg-amber-500 px-8 py-4 text-xs font-bold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-400"
          >
            Créer mon compte artiste
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
          <p className="mt-4 text-xs text-zinc-500">
            À l'inscription, cochez « Je suis un artiste » — la validation se fait à l'écoute.
          </p>
        </div>
      </Reveal>
    </main>
  );
};

export default DevenirArtiste;
